import React, { useState, useEffect } from 'react';

// Material
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

// Hooks
import useWarehouse from '../../../hooks/warehouse/useWarehouse';

// Components
import RefundItem from './refundItem';
import Modal from '../../../components/modal/order/index';
import { OrderStates } from '../../../models/OrderEntity';
import { Checkbox } from '@mui/material';
import { utils } from '../../../utils/utils';
import AutocompleteConcepts from '../../../components/inputs/autocompletes/AutocompleteConcepts';
import { Alert } from '@mui/material';

const RefundModal = ({ order, onSucces, onClose, open, openMode, setRefund }) => {
    const [refundItems, setRefundItems] = useState([]);
    const [isManualInvoice, setIsManualInvoice] = useState(false);
    const { warehouses } = useWarehouse({ isExport: true });
    const [generalConcept, setGeneralConcept] = useState(null);
    const isTotalSelection = openMode === OrderStates.Voided || openMode === OrderStates.InvoiceInitiated;

    const handleAutocompleteChange = (_, options) => {
        setGeneralConcept(options);
        onItemGeneralChange(options);
    }

    useEffect(
        () => {
            const items = openMode === OrderStates.InvoiceInitiated ? order.itemsToReturn : order.items
            const refunds = items
                .filter(item => (item.quantity - (item.refundedQuantity !== undefined ? item.refundedQuantity : 0)) > 0)
                .map(item =>
                ({
                    ...item,
                    refundQuantity: 0,
                    selected: false
                }));
            setRefundItems(refunds);
        }, [order.items]);

    const onItemChange = (refId, warehouseId, quantity, concept) => {
        const items = refundItems.map(item => {
            if (refId !== item.refId || warehouseId !== item.warehouseId)
                return item;

            return {
                ...item,
                refundQuantity: quantity,
                selected: true,
                conceptId: concept?.id
            }
        });

        setRefundItems(items);
    };

    const onItemGeneralChange = (concept) => {
        const items = refundItems.map(item => {
            return {
                ...item,
                conceptId: concept?.id
            };
        });
        setRefundItems(items);
    };

    const refundAmount = () =>
        refundItems
            .map(item => item.selected || isTotalSelection ? calculateRefound(item) : 0)
            .reduce((a, b) => a + b, 0)

    const onItemRemoveSelected = (refId, warehouseId) => {
        const items = refundItems.map(item => {
            if (refId !== item.refId || warehouseId !== item.warehouseId)
                return item;

            return {
                ...item,
                refundQuantity: 0,
                selected: false,
                conceptId: null
            }
        });

        setRefundItems(items);
    };

    const calculateRefound = (item) => {
        const discount = item.refundQuantity / item.quantity * item.discount
        return (item.refundQuantity * item.unitPrice) - discount
    }

    const handlerOnAccept = () => {
        const refundData = refundItems
            .filter(item => item.selected || isTotalSelection)
            .map(item => ({
                RefId: item.refId,
                Quantity: isTotalSelection
                ? (item.quantity - (item.refundedQuantity ?? 0))
                : item.refundQuantity,
                WarehouseId: item.warehouseId,
                conceptId: item.conceptId
            }));
        
        setRefund(refundData);
        onSucces(refundData, isManualInvoice, refundAmount());
    }

    const disabledSave = () => {
        if ((openMode === OrderStates.Refunded || openMode === OrderStates.Voided || openMode === OrderStates.InvoiceInitiated)) {
            let anySelectedWithoutConceptId = false;
            let noItemsSelected = true;

            for (const item of refundItems) {
                if (item.selected || isTotalSelection) {
                    noItemsSelected = false;
                    if (!item.conceptId) {
                        anySelectedWithoutConceptId = true;
                        break;
                    }
                }
            }
            return anySelectedWithoutConceptId || noItemsSelected;
        }

        return !refundItems.some(item => item.selected)
    }

    const diffDates = (created) => {
        if (created !== undefined) {
            const date = new Date();
            const dateCreated = new Date(created);
            const diffTime = Math.abs(date - dateCreated);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            return diffDays;
        }
        return 0;
    }

    const getWarehouseName = (warehouseId) => {
        const warehouse = warehouses?.find(w => w.id === warehouseId);
        return warehouse?.name ?? ""
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={(openMode === OrderStates.Refunded || openMode === OrderStates.Voided) ? 'Devoluciones' : 'Cambios'}
            contentProps={{ dividers: true }}
            dates={diffDates(order.created)}
            customButtons={() =>
                <DialogActions>

                    {(openMode === OrderStates.Refunded || openMode === OrderStates.Voided) &&
                        <>
                            <Checkbox
                                color='primary'
                                name='replaced'
                                onChange={(event) => setIsManualInvoice(event.target.checked)}
                                checked={isManualInvoice}
                            /> Fac. manual
                        </>}
                    <Button
                        onClick={onClose}
                        color="primary"
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={handlerOnAccept}
                        color="primary"
                        variant='outlined'
                        autoFocus
                        disabled={disabledSave()}
                    >
                        Guardar
                    </Button>
                </DialogActions>}
        >
            <Grid container spacing={1} style={{ marginBottom: '20px' }}>
                {(openMode === OrderStates.Refunded || openMode === OrderStates.Voided || openMode === OrderStates.InvoiceInitiated) &&
                    <>
                        <Grid size={12}>
                            <AutocompleteConcepts
                                style={{ paddingBottom: '10px' }}
                                multiple={false}
                                fullWidth
                                limitTags={4}
                                onChange={handleAutocompleteChange}
                                value={generalConcept}
                                type={openMode === OrderStates.Refunded || openMode === OrderStates.Voided ? 'Void' : 'Change'}
                            />
                        </Grid>
                        <Grid size={12}>
                            <Alert variant="outlined" severity="info" style={{ width: '100%' }}>
                                Los motivos <strong>serán generales</strong> para todos los ítems.
                            </Alert>
                        </Grid>
                    </>
                }
            </Grid>
            <Grid container spacing={1}>
                {
                    refundItems.map((item, index) => (
                        <Grid key={index} size={12}>
                            <RefundItem
                                generalConcept={generalConcept}
                                item={{ ...item, warehouseName: getWarehouseName(item.warehouseId) }}
                                onChange={onItemChange}
                                onItemGeneralChange={onItemGeneralChange}
                                onRemoveSelected={onItemRemoveSelected}
                                currency={order.currency}
                                openMode={openMode}
                            />
                        </Grid>
                    ))
                }
                {openMode !== OrderStates.InvoiceInitiated &&
                    <>
                        <Grid size={6}>
                            <Typography variant='body1' display='block' align='right'>
                                Total a devolver:
                            </Typography>
                        </Grid>
                        <Grid size={6}>
                            <Typography variant='body1' display='block' align='right'>
                                {order.currency} <strong>{utils.formatMoney(refundAmount())}</strong>
                            </Typography>
                        </Grid>
                    </>
                }
            </Grid>
        </Modal>
    );
}

export default RefundModal;